import React from 'react';

interface PerformanceRow {
  channel: string;
  spend: number;
  spendShare: number;
  effect: number;
  effectShare: number;
  roas: number;
}

interface EffectAndPerformanceProps {
  data: PerformanceRow[] | undefined;
  title?: string;
}

// Helper to sanitize and normalize numeric values
const sanitizeValue = (value: unknown, defaultValue: number = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  if (isNaN(num) || !isFinite(num)) return defaultValue;
  return num;
};

// Helper to format currency
const formatCurrency = (value: number): string => {
  const sanitized = sanitizeValue(value, 0);
  return `$${sanitized.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

// Helper to format percentage - expects value to already be 0-100 range
const formatPercentage = (value: number): string => {
  const sanitized = sanitizeValue(value, 0);
  // If value appears to be > 1000%, it's likely not in percentage format (0-100)
  // Warn and clamp it
  if (Math.abs(sanitized) > 1000) {
    console.warn(`Percentage value appears corrupted: ${sanitized}, clamping to 0%`);
    return '0.0%';
  }
  return `${Math.max(0, Math.min(100, sanitized)).toFixed(1)}%`;
};

// Helper to format ROAS
const formatROAS = (value: number): string => {
  const sanitized = sanitizeValue(value, 0);
  // ROAS should be reasonable - if > 1000, it's likely corrupted
  if (Math.abs(sanitized) > 1000) {
    console.warn(`ROAS value appears corrupted: ${sanitized}, showing N/A`);
    return 'N/A';
  }
  return sanitized < 0 ? `$${sanitized.toFixed(2)}` : `$${sanitized.toFixed(2)}`;
};

export const EffectAndPerformance: React.FC<EffectAndPerformanceProps> = ({
  data,
  title = 'Effect & Performance Metrics by Channel',
}) => {
  // Check if data is an array and has items
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-96 bg-gray-50 rounded">
          <p className="text-gray-500">No performance metrics available from model results</p>
        </div>
      </div>
    );
  }

  // Sanitize all input data
  const metrics = data.map(m => ({
    channel: m.channel || 'Unknown',
    spend: sanitizeValue(m.spend, 0),
    spendShare: sanitizeValue(m.spendShare, 0),
    effect: sanitizeValue(m.effect, 0),
    effectShare: sanitizeValue(m.effectShare, 0),
    roas: sanitizeValue(m.roas, 0),
  }));

  const totalSpend = metrics.reduce((sum, m) => sum + m.spend, 0);
  const totalEffect = metrics.reduce((sum, m) => sum + m.effect, 0);
  const avgROAS = totalSpend > 0 ? totalEffect / totalSpend : 0;

  // Sort by ROAS descending for better readability
  const sortedMetrics = [...metrics].sort((a, b) => b.roas - a.roas);

  return (
    <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-semibold text-gray-900">Channel</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">Spend</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">Spend %</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">Effect</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">Effect %</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">ROAS</th>
            </tr>
          </thead>
          <tbody>
            {sortedMetrics.map((metric, idx) => (
              <tr
                key={metric.channel}
                className={`border-b border-gray-200 hover:bg-gray-50 ${
                  idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                <td className="px-4 py-3 font-medium text-gray-900">{metric.channel}</td>
                <td className="px-4 py-3 text-right text-gray-700">
                  {formatCurrency(metric.spend)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {formatPercentage(metric.spendShare)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-gray-700">
                  {formatCurrency(metric.effect)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {formatPercentage(metric.effectShare)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      avgROAS > 0 && metric.roas > avgROAS
                        ? 'bg-emerald-100 text-emerald-800'
                        : avgROAS > 0 && metric.roas > avgROAS * 0.8
                        ? 'bg-yellow-100 text-yellow-800'
                        : metric.roas < 0
                        ? 'bg-red-100 text-red-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}
                  >
                    {formatROAS(metric.roas)}
                  </span>
                </td>
              </tr>
            ))}
            <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-t-2 border-gray-300 font-semibold">
              <td className="px-4 py-3 text-gray-900">Total / Avg</td>
              <td className="px-4 py-3 text-right text-gray-900">
                {formatCurrency(totalSpend)}
              </td>
              <td className="px-4 py-3 text-right text-gray-900">100.0%</td>
              <td className="px-4 py-3 text-right text-gray-900">
                {formatCurrency(totalEffect)}
              </td>
              <td className="px-4 py-3 text-right text-gray-900">100.0%</td>
              <td className="px-4 py-3 text-right text-gray-900">{formatROAS(avgROAS)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-600 font-semibold mb-1">TOTAL SPEND</p>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalSpend)}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <p className="text-xs text-green-600 font-semibold mb-1">TOTAL EFFECT</p>
          <p className="text-2xl font-bold text-green-900">
            {formatCurrency(totalEffect)}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <p className="text-xs text-purple-600 font-semibold mb-1">AVG ROAS</p>
          <p className="text-2xl font-bold text-purple-900">{formatROAS(avgROAS)}</p>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <p>
          <strong>Interpretation:</strong> Shows performance metrics for each channel:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>
            <strong>Spend %:</strong> Percentage of total budget allocated to this channel
          </li>
          <li>
            <strong>Effect %:</strong> Percentage of total revenue impact attributed to this channel
          </li>
          <li>
            <strong>ROAS:</strong> Return on Ad Spend (revenue impact per dollar spent). Higher is better.
          </li>
        </ul>
        <p className="mt-2">
          Channels with Effect % higher than Spend % are performing better than average ROI. Consider increasing budget
          allocation to these channels.
        </p>
      </div>
    </div>
  );
};
